import { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { MuiEvent } from '@mui/x-data-grid'

import { OpenFormProps } from '@pages/ConfiguredEntity/types'
import { useSubscribeVariablesChange } from '@pages/Window/hooks'
import { FormEventType, FormTabType } from '@pages/WindowsCreateOrEdit/types'

import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'

import { useAppDispatch, useReplaceVariablesInFilter } from '@hooks'
import {
  filterActionsByActionCode,
  filterEventsByEventCode,
  getInitialPositionWindow,
  ReturnTransformCommonParametersToValues,
  showSnackbarNotification,
} from '@helpers'
import {
  ACTION_CODE,
  CLICK_EVENT_TYPE,
  EVENT_CODE,
  FIELD_BIND_TYPE_ENUM,
  KIND_SEARCHABLE_FILTER_WORD,
  WINDOW_ENTITY,
} from '@constants'
import { DIALOG_WINDOW_TYPE, DialogWindowType, GETAllVariables, ObjectDataRecord } from '@types'

import { EVENT_NAME, LocalEventBus } from '../../../../../events'

type TProps = {
  activeTab: FormTabType
  commonParameters: ReturnTransformCommonParametersToValues
  dialogWindows: DialogWindowType[]
  currentDialogWindow: DialogWindowType<Record<string, unknown>> | undefined
  windowVariables?: GETAllVariables
  localEventBus: LocalEventBus
}

export const useSection = ({
  activeTab,
  commonParameters,
  dialogWindows,
  currentDialogWindow,
  windowVariables = [],
  localEventBus,
}: TProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { updatedVariables } = useSubscribeVariablesChange({
    windowVariables,
    event: EVENT_NAME.ON_ROW_SELECTION,
    localEventBus,
  })

  const { updatedFilter } = useReplaceVariablesInFilter({
    rawFilter: activeTab?.conditions,
    latestVariables: updatedVariables,
    globalVariables: [],
    searchableFilterWord: KIND_SEARCHABLE_FILTER_WORD.WINDOW,
  })

  const handleOpenForm = (
    { entityType, ...formProps }: OpenFormProps,
    muiEvent: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    type: CLICK_EVENT_TYPE
  ) => {
    const { id, formCode, viewCode, objectId, path, event } = formProps
    const initialPosition = getInitialPositionWindow(muiEvent)
    const openedFormsCount = dialogWindows.filter(
      dialog =>
        [DIALOG_WINDOW_TYPE.FORM, DIALOG_WINDOW_TYPE.VIEW].includes(dialog.type) && !dialog.hidden
    ).length
    const maxOpenedFormsCount = Number(commonParameters.numberOfWindows)

    if (openedFormsCount === maxOpenedFormsCount && type !== CLICK_EVENT_TYPE.CREATE_CLICK) {
      showSnackbarNotification({
        text: t('error.dynamicFormsExceeded', { count: maxOpenedFormsCount }),
        options: {
          variant: 'info',
        },
      })

      return
    }

    dispatch(
      openFormDialog({
        id,
        parentDialogId: currentDialogWindow ? currentDialogWindow.id : null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          formCode,
          objectId,
          viewCode,
          path,
          event,
          globalId: id,
          entityType,
        },
        initialPosition,
      })
    )

    return
  }

  const handleRowSelection = (selectedRow: ObjectDataRecord | null) => {
    if (activeTab.entityType !== WINDOW_ENTITY.VIEW || activeTab.actions.length === 0) return

    const arrayVariables = [...windowVariables]

    const allPostValuesActions = filterActionsByActionCode(
      activeTab.actions,
      ACTION_CODE.POST_VALUE
    )

    const allEvents: FormEventType[] = allPostValuesActions.flatMap(action => action.events)

    const eventsWithRowSelection = filterEventsByEventCode(allEvents, EVENT_CODE.ON_ROW_SELECTION)

    const srcObj = {
      windowVariables,
      rowData: selectedRow,
    }

    eventsWithRowSelection.forEach(event => {
      const isExistsVariable = arrayVariables.findIndex(variable => variable.id === event.varId)

      if (isExistsVariable !== -1 && typeof event.value === 'string') {
        const prepareNameField = event.value.includes('.') ? event.value.split('.')[0] : event.value

        const newValueVariable =
          event.valueType === FIELD_BIND_TYPE_ENUM.JS
            ? new Function('srcObj', event.value)(srcObj)
            : selectedRow?.[prepareNameField] ?? null

        const updatedVariable = {
          ...arrayVariables[isExistsVariable],
          defaultValue: newValueVariable,
        }

        localEventBus.publish(EVENT_NAME.ON_ROW_SELECTION, updatedVariable)
      }
    })
  }

  return {
    data: {
      updatedFilter,
    },
    state: {},
    handlers: {
      handleOpenForm,
      handleRowSelection,
    },
  }
}
